import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { MOBILE } from '../../../utils/constants';

interface Props {
  avatar: {
    initials: string;
    backgroundColor: string;
  };
  name: string;
  badge?: string;
  timeAgo: string;
}

const PostAuthor = ({ avatar, name, badge, timeAgo }: Props) => {
  return (
    <div css={containerStyles} data-testid="post-author">
      <div css={avatarContainerStyles}>
        <div css={avatarStyles} style={{ backgroundColor: avatar.backgroundColor }} data-testid="author-avatar">
          {avatar.initials}
        </div>
      </div>
      <div css={infoContainerStyles}>
        <div css={nameRowStyles}>
          <a
            href={`/profile/${encodeURIComponent(name)}`}
            css={nameStyles}
            onClick={(e) => e.preventDefault()}
            data-testid="author-name"
          >
            {name}
          </a>
          {badge && (
            <span css={badgeStyles} data-testid="author-badge">
              {badge}
            </span>
          )}
        </div>
        <div css={timeStyles} data-testid="post-time">
          {timeAgo}
        </div>
      </div>
    </div>
  );
};

const containerStyles = css({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '12px',
  marginBottom: '16px',
  fontFamily: 'Inter, sans-serif',
  [MOBILE.MEDIA_QUERY]: {
    gap: '8px',
    marginBottom: '12px',
  },
});

const avatarContainerStyles = css({
  flexShrink: 0,
});

const avatarStyles = css({
  width: '48px',
  height: '48px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: token('color.text.inverse'),
  fontSize: '18px',
  fontWeight: 700,
  backgroundColor: token('color.background.discovery.bold'),
  [MOBILE.MEDIA_QUERY]: {
    width: '36px',
    height: '36px',
    fontSize: '15px',
  },
});

const infoContainerStyles = css({
  flex: 1,
  minWidth: 0,
});

const nameRowStyles = css({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '2px',
});

const nameStyles = css({
  fontSize: '16px',
  fontWeight: 700,
  color: token('color.text'),
  textDecoration: 'none',
  fontFamily: 'Charlie Text, sans-serif',
  '&:hover': {
    textDecoration: 'underline',
  },
  [MOBILE.MEDIA_QUERY]: {
    fontSize: '14px',
  },
});

const badgeStyles = css({
  fontSize: '11px',
  fontWeight: 700,
  color: token('color.text.subtle'),
  backgroundColor: token('color.background.neutral'),
  padding: '2px 6px',
  borderRadius: '3px',
  textTransform: 'uppercase',
  letterSpacing: '0.3px',
  [MOBILE.MEDIA_QUERY]: {
    fontSize: '10px',
    padding: '2px 4px',
  },
});

const timeStyles = css({
  fontSize: '14px',
  color: token('color.text.subtle'),
  [MOBILE.MEDIA_QUERY]: {
    fontSize: '12px',
  },
});

export default PostAuthor;
